/**
 * Turning a distance into a verdict.
 *
 * Win and grade are deliberately separate concerns. *Winning* is generous,
 * because losing a streak is the most expensive thing that can happen to a
 * daily game. *Grading* is strict, because that is where a regular player
 * spends their ambition.
 */

import { deltaE } from '../core/colour';

export type Grade = 'perfect' | 'excellent' | 'great' | 'close' | 'miss';

export const GRADE_ORDER: readonly Grade[] = ['perfect', 'excellent', 'great', 'close', 'miss'];

interface GradeMeta {
  label: string;
  /** One line, shown on the reveal. */
  blurb: string;
  symbol: string;
  /** Tailwind-free CSS token name for the grade colour. */
  token: string;
}

export const GRADES: Record<Grade, GradeMeta> = {
  perfect: { label: 'Perfect', blurb: 'Indistinguishable. Show off.', symbol: '✦', token: 'perfect' },
  excellent: { label: 'Excellent', blurb: 'A trained eye would struggle.', symbol: '◆', token: 'excellent' },
  great: { label: 'Great', blurb: 'Same family, clearly close.', symbol: '●', token: 'great' },
  close: { label: 'Close', blurb: 'In the neighbourhood.', symbol: '◐', token: 'close' },
  miss: { label: 'Miss', blurb: 'Not this time.', symbol: '○', token: 'miss' },
};

export function gradeFor(distance: number, tolerance: number): Grade {
  if (!Number.isFinite(distance)) return 'miss';
  if (distance <= Math.max(1.5, tolerance * 0.3)) return 'perfect';
  if (distance <= Math.max(3, tolerance * 0.5)) return 'excellent';
  if (distance <= tolerance * 0.72) return 'great';
  if (distance <= tolerance) return 'close';
  return 'miss';
}

export function isWin(grade: Grade): boolean {
  return grade !== 'miss';
}

export interface Score {
  distance: number;
  tolerance: number;
  grade: Grade;
  win: boolean;
  /** 0..1, how tight the match was. 1 is perfect. */
  precision: number;
}

export function score(lab: { L: number; a: number; b: number }, target: { L: number; a: number; b: number }, tolerance: number): Score {
  const distance = deltaE(lab, target);
  const grade = gradeFor(distance, tolerance);
  return {
    distance,
    tolerance,
    grade,
    win: isWin(grade),
    precision: Math.max(0, 1 - distance / (tolerance * 2)),
  };
}

/**
 * Distance as a phrase, because "delta E 4.31" means nothing to most people
 * and a screen reader should not have to say it either.
 */
export function describeDistance(distance: number): string {
  if (!Number.isFinite(distance)) return 'unmeasured';
  if (distance <= 1.5) return 'a hair';
  if (distance <= 3) return 'a whisker';
  if (distance <= 6) return 'a shade off';
  if (distance <= 12) return 'a step off';
  if (distance <= 22) return 'a few steps off';
  return 'a long way off';
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
